/**
 * The full table behind the rail's overflow link (`AccountsRail.tsx`): every
 * account this profile resolves to, sortable per column, paged against the
 * real `GET .../monitoring-profiles/{id}/accounts` route
 * (`api/src/socialsrc_api/routers/monitoring_profiles.py`) rather than the
 * rail's own fetch -- opening page two here never moves the rail, because
 * each keeps its own query key and its own `page`.
 *
 * Sorting is server-side: a column click sends the route's own `sort` query
 * parameter and re-fetches page 1, so it orders every account the profile
 * has, not only the page already loaded.
 *
 * `useProfileAccountsPage`/`ProfileAccount` live here, the leaf of the two
 * files, so `AccountsRail.tsx` can import them without this file importing
 * back.
 */

import { useEffect, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { ColumnDef, SortingState } from '@tanstack/react-table';
import { z } from 'zod';

import type { ApiFailure, ApiFailureError } from '../../../../lib/http/errors';
import { Absent } from '../../../../chrome/measure';
import { EmptyState, ErrorState } from '../../../../chrome/state';
import { DataTable, SortableHeader } from '../../../../chrome/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../../ui/dialog';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationNext,
  PaginationPrevious,
} from '../../../../ui/pagination';
import { GET_ROUTES, routePath } from '../../../../lib/http/routes';
import { useApiClient } from '../../../session/client';
import { accountsPageSchema, profileAccountSchema } from '../../api/profileSchemas';
import { profileCatalogue } from './messages';

const at = profileCatalogue.t;

/** The rail draws its first N accounts as its own page 1 -- see
 *  `AccountsRail.tsx`'s own `RAIL_WINDOW`, which this stays paired with only
 *  by both reading the same real route, never by a shared constant: the two
 *  surfaces page independently on purpose. */
const MODAL_PAGE_SIZE = 5;

export type ProfileAccount = z.infer<typeof profileAccountSchema>;

/** The route's own `sort` values (`api/src/socialsrc_api/routers/
 *  monitoring_profiles.py`'s `ProfileAccountSort`). */
export type ProfileAccountSort = 'handle' | '-handle' | 'campaign_name' | '-campaign_name';

const DEFAULT_SORT: ProfileAccountSort = 'handle';

/** The table's own `SortingState` (at most one column, this table sorts by
 *  one column at a time) collapsed to the route's `sort` value. */
export function sortParam(sorting: SortingState): ProfileAccountSort {
  const [first] = sorting;
  if (first === undefined) return DEFAULT_SORT;
  const column: ProfileAccountSort = first.id === 'campaign_name' ? 'campaign_name' : 'handle';
  return first.desc ? (`-${column}` as ProfileAccountSort) : column;
}

export const PROFILE_ACCOUNTS_QUERY_KEY = (
  tenantId: string,
  profileId: string,
  page: number,
  pageSize: number,
  sort: ProfileAccountSort,
) => ['control', 'monitoring-profile-accounts', tenantId, profileId, page, pageSize, sort] as const;

export function useProfileAccountsPage(
  tenantId: string,
  profileId: string,
  page: number,
  pageSize: number,
  sort: ProfileAccountSort = DEFAULT_SORT,
  // The modal stays mounted (inside `AccountsRail`) whether it is open or
  // not, so its own fetch is gated on `open` rather than firing the moment
  // the rail does; the rail itself always wants its page, so it leaves this
  // at the default.
  enabled = true,
) {
  const client = useApiClient();
  return useQuery<z.infer<typeof accountsPageSchema>, ApiFailureError>({
    queryKey: PROFILE_ACCOUNTS_QUERY_KEY(tenantId, profileId, page, pageSize, sort),
    queryFn: () =>
      client.getParsed(
        routePath(
          GET_ROUTES.monitoringProfileAccounts,
          { tenantId, profileId },
          { page: String(page), page_size: String(pageSize), sort },
        ),
        GET_ROUTES.monitoringProfileAccounts.schema,
      ),
    enabled,
  });
}

const COLUMNS: ColumnDef<ProfileAccount, unknown>[] = [
  {
    accessorKey: 'handle',
    header: ({ column }) => (
      <SortableHeader column={column}>{at('accounts.column.account')}</SortableHeader>
    ),
    cell: (info) =>
      info.getValue<ProfileAccount['handle']>() ?? (
        <Absent reason={{ text: at('accounts.handleNone') }} />
      ),
  },
  {
    accessorKey: 'campaign_name',
    header: ({ column }) => (
      <SortableHeader column={column}>{at('accounts.column.campaign')}</SortableHeader>
    ),
    cell: (info) => info.getValue<ProfileAccount['campaign_name']>(),
  },
];

export function AccountsModal(props: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tenantId: string;
  profileId: string;
  /** Forwarded to `DialogContent`'s own `onCloseAutoFocus`: this dialog has
   *  no `DialogTrigger` (its opener lives in the caller, `AccountsRail`), so
   *  Radix has no element of its own to return focus to on close -- the
   *  caller supplies one explicitly instead of the trap silently dropping
   *  focus to the document body. */
  onCloseAutoFocus?: (event: Event) => void;
}): ReactNode {
  const [page, setPage] = useState(1);
  const [sorting, setSorting] = useState<SortingState>([]);
  // Every reopen starts back at page 1, unsorted -- state kept from the last
  // time this was open would show stale numbering and order the first
  // render, before the fetch for that page even lands.
  useEffect(() => {
    if (props.open) {
      setPage(1);
      setSorting([]);
    }
  }, [props.open]);

  const sort = sortParam(sorting);
  const pageQuery = useProfileAccountsPage(
    props.tenantId,
    props.profileId,
    page,
    MODAL_PAGE_SIZE,
    sort,
    props.open,
  );
  const total = pageQuery.data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / MODAL_PAGE_SIZE));
  const failure: ApiFailure | undefined = pageQuery.error?.failure;

  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange}>
      <DialogContent size="lg" onCloseAutoFocus={props.onCloseAutoFocus}>
        <DialogHeader>
          <DialogTitle>{at('accounts.modal.title')}</DialogTitle>
        </DialogHeader>
        {failure !== undefined ? (
          <ErrorState failure={failure} />
        ) : (
          <>
            <DataTable<ProfileAccount>
              columns={COLUMNS}
              data={pageQuery.data?.items ?? []}
              loading={pageQuery.data === undefined}
              getRowId={(row) => row.id}
              caption={at('accounts.table.caption')}
              emptyState={<EmptyState title={at('accounts.empty')} />}
              sorting={sorting}
              onSortingChange={(next) => {
                setSorting(next);
                setPage(1);
              }}
            />
            {pageQuery.data !== undefined && pageCount > 1 && (
              <Pagination aria-label={at('accounts.pagination.label')} className="justify-end">
                <PaginationContent>
                  <PaginationItem>
                    <PaginationPrevious
                      href="#"
                      aria-label={at('accounts.pagination.previous')}
                      aria-disabled={page <= 1}
                      tabIndex={page <= 1 ? -1 : undefined}
                      className={page <= 1 ? 'pointer-events-none opacity-50' : undefined}
                      onClick={(event) => {
                        event.preventDefault();
                        setPage((current) => Math.max(1, current - 1));
                      }}
                    >
                      {at('accounts.pagination.previous')}
                    </PaginationPrevious>
                  </PaginationItem>
                  <PaginationItem>
                    <span className="text-caption text-muted-foreground px-2" data-numeric>
                      {at('accounts.pagination.page', { page, pages: pageCount })}
                    </span>
                  </PaginationItem>
                  <PaginationItem>
                    <PaginationNext
                      href="#"
                      aria-label={at('accounts.pagination.next')}
                      aria-disabled={page >= pageCount}
                      tabIndex={page >= pageCount ? -1 : undefined}
                      className={page >= pageCount ? 'pointer-events-none opacity-50' : undefined}
                      onClick={(event) => {
                        event.preventDefault();
                        setPage((current) => Math.min(pageCount, current + 1));
                      }}
                    >
                      {at('accounts.pagination.next')}
                    </PaginationNext>
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
